<template>
  <div class="page flex-col">
    <AppHeader />

    <!-- Banner -->
    <div class="sol-banner">
      <div class="sol-banner-mask">
        <span class="sol-banner-zh">{{ isEnglish ? 'Solutions' : '解决方案' }}</span>
        <span class="sol-banner-en">{{ isEnglish ? 'Offshore Wind' : 'Solution' }}</span>
      </div>
    </div>

    <div class="sol-main">
      <!-- 面包屑（右对齐，版心内） -->
      <nav class="sol-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="sol-crumb-sep">&gt;</span>
        <span>{{ isEnglish ? 'Solutions' : '解决方案' }}</span>
        <span class="sol-crumb-sep">&gt;</span>
        <span class="sol-crumb-active">{{ pageTitle }}</span>
      </nav>

      <!-- 页标题 -->
      <h1 class="sol-page-title">{{ pageTitle }}</h1>
      <div class="sol-page-line"></div>

      <!-- 一、项目概况 -->
      <h2 class="sol-h2">{{ isEnglish ? 'I. Project Overview' : '一、项目概况' }}</h2>
      <div class="sol-overview">
        <div
          v-for="(item, index) in displayOverviewList"
          :key="'ov_' + index"
          class="sol-overview-col"
        >
          <div class="sol-ov-label">{{ item.label }}</div>
          <p class="sol-ov-desc">{{ item.desc }}</p>
          <div class="sol-ov-metric">
            <span class="sol-ov-num">{{ item.num }}</span>
            <span class="sol-ov-unit">{{ item.unit }}</span>
          </div>
          <div class="sol-ov-sub">{{ item.sub }}</div>
        </div>
      </div>

      <!-- 二、腐蚀挑战 -->
      <h2 class="sol-h2">{{ isEnglish ? 'II. Corrosion Challenges' : '二、腐蚀挑战' }}</h2>
      <div class="sol-challenge">
        <div class="sol-chart-card">
          <div class="sol-chart-legend">
            <span class="sol-legend-item">
              <i class="sol-dot sol-dot-dark"></i>{{ isEnglish ? 'Standard coating system' : '标准涂装体系' }}
            </span>
            <span class="sol-legend-item">
              <i class="sol-dot sol-dot-teal"></i>{{ isEnglish ? 'Duplex dual-layer system' : 'Duplex双层复合体系' }}
            </span>
          </div>
          <div class="sol-chart-body">
            <div class="sol-chart-y">
              <span>20</span><span>15</span><span>10</span><span>5</span><span>0</span>
            </div>
            <div class="sol-chart-plot">
              <div class="sol-bars">
                <div class="sol-bar-wrap">
                  <span class="sol-bar-val">5</span>
                  <div class="sol-bar sol-bar-dark" style="height: 25%"></div>
                </div>
                <div class="sol-bar-wrap">
                  <span class="sol-bar-val">20</span>
                  <div class="sol-bar sol-bar-teal" style="height: 100%"></div>
                </div>
              </div>
              <div class="sol-chart-xline"></div>
            </div>
          </div>
          <div class="sol-chart-caption">
            {{ isEnglish ? 'Coating design life comparison (years)' : '涂层设计寿命对比（年）' }}
          </div>
        </div>

        <div class="sol-challenge-list">
          <div
            v-for="(item, index) in displayChallengeList"
            :key="'ch_' + index"
            class="sol-challenge-item"
          >
            <div class="sol-challenge-icon">
              <img :src="item.icon" :alt="item.title" />
            </div>
            <div class="sol-challenge-text">
              <div class="sol-challenge-title">{{ item.title }}</div>
              <div class="sol-challenge-desc">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 三、解决方案 -->
      <h2 class="sol-h2">
        {{ isEnglish
          ? 'III. Solution: Duplex Dual-Layer Protection System'
          : '三、解决方案：Duplex 双层复合防护体系' }}
      </h2>
      <div class="sol-core-tip">
        <i class="sol-core-bullet"></i>
        <span v-if="isEnglish"><em>Core mechanism</em>: electrochemical protection + physical barrier</span>
        <span v-else><em>核心机制</em>：电化学保护 + 物理屏蔽双重防护</span>
      </div>
      <div class="sol-duplex">
        <div class="sol-formula">
          <div class="sol-formula-card">
            <div class="sol-formula-title">{{ isEnglish ? 'Thermal spray metal coating' : '热喷涂金属涂层' }}</div>
            <div class="sol-formula-desc">
              {{ isEnglish
                ? 'A dense metal layer on the substrate providing sacrificial anode cathodic protection'
                : '基材表面形成致密金属层，提供牺牲阳极式阴极保护' }}
            </div>
          </div>
          <span class="sol-formula-op">+</span>
          <div class="sol-formula-card">
            <div class="sol-formula-title">{{ isEnglish ? 'Sealant & topcoat' : '封孔剂加面漆' }}</div>
            <div class="sol-formula-desc">
              {{ isEnglish
                ? 'Seals coating micropores and forms a dense physical barrier against media ingress'
                : '封闭涂层微孔并构成致密物理屏障，阻断介质渗透' }}
            </div>
          </div>
          <span class="sol-formula-op">=</span>
          <div class="sol-formula-card sol-formula-result">
            <div class="sol-result-num">20<span>{{ isEnglish ? 'yrs' : '年' }}</span></div>
            <div class="sol-result-desc">{{ isEnglish ? '20+ years protection' : '以上长效防护' }}</div>
          </div>
        </div>

        <div class="sol-steps-head">
          <span class="sol-steps-label">{{ isEnglish ? 'Three-step delivery' : '落地三步法' }}</span>
          <span class="sol-steps-arrow">→</span>
        </div>
        <div class="sol-steps">
          <div
            v-for="(step, index) in displayDuplexSteps"
            :key="'st_' + index"
            class="sol-step"
          >
            <span class="sol-step-num">{{ index + 1 }}</span>
            <div class="sol-step-body">
              <div class="sol-step-title">{{ step.title }}</div>
              <div class="sol-step-desc">{{ step.desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 四、分区热喷涂方案 -->
      <h2 class="sol-h2">{{ isEnglish ? 'IV. Zoned Thermal Spray Scheme' : '四、分区热喷涂方案' }}</h2>
      <div class="sol-zone">
        <div class="sol-chart-card sol-zone-chart">
          <div class="sol-chart-legend">
            <span class="sol-legend-item">
              <i class="sol-dot" style="background:#1e40af"></i>
              {{ isEnglish ? 'Splash / immersion' : '潮差/全浸区' }}
            </span>
            <span class="sol-legend-item">
              <i class="sol-dot" style="background:#0ea5e9"></i>
              {{ isEnglish ? 'Atmospheric zone' : '大气区' }}
            </span>
            <span class="sol-legend-item">
              <i class="sol-dot" style="background:#14b8a6"></i>
              {{ isEnglish ? 'High-stress areas' : '高应力区' }}
            </span>
          </div>
          <div class="sol-chart-body">
            <div class="sol-chart-y">
              <span>350</span><span>280</span><span>210</span><span>140</span><span>70</span><span>0</span>
            </div>
            <div class="sol-chart-plot">
              <div class="sol-bars sol-bars-3">
                <div class="sol-bar-wrap">
                  <span class="sol-bar-val">220</span>
                  <div class="sol-bar" style="height:63%;background:#1e40af"></div>
                </div>
                <div class="sol-bar-wrap">
                  <span class="sol-bar-val">130</span>
                  <div class="sol-bar" style="height:37%;background:#0ea5e9"></div>
                </div>
                <div class="sol-bar-wrap">
                  <span class="sol-bar-val">350</span>
                  <div class="sol-bar" style="height:100%;background:#14b8a6"></div>
                </div>
              </div>
              <div class="sol-chart-xline"></div>
            </div>
          </div>
          <div class="sol-chart-caption">
            {{ isEnglish ? 'Coating thickness by zone (μm)' : '各区域涂层厚度对比（μm）' }}
          </div>
        </div>

        <div class="sol-zone-right">
          <div
            v-for="(z, index) in displayZoneList"
            :key="'zn_' + index"
            class="sol-zone-card"
          >
            <div class="sol-zone-title">{{ z.title }}</div>
            <div class="sol-zone-desc">{{ z.desc }}</div>
          </div>
          <div class="sol-zone-summary">
            <strong>{{ isEnglish ? 'Total DFT 430–500μm' : '总膜厚 430–500μm' }}</strong>
            <span>
              {{ isEnglish
                ? 'Primer + intermediate + topcoat integrated protection'
                : '底 + 中 + 面 三位一体防护体系' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 五、效益与商业优势 -->
      <h2 class="sol-h2">{{ isEnglish ? 'V. Benefits & Commercial Advantages' : '五、效益与商业优势' }}</h2>
      <div class="sol-benefit">
        <div class="sol-chart-card sol-benefit-chart">
          <div class="sol-chart-legend">
            <span class="sol-legend-item">
              <i class="sol-dot sol-dot-dark"></i>
              {{ isEnglish ? 'Longer maintenance intervals' : '维修周期长' }}
            </span>
            <span class="sol-legend-item">
              <i class="sol-dot sol-dot-teal"></i>
              {{ isEnglish ? 'Lower lifecycle cost' : '全生命周期成本降低' }}
            </span>
          </div>
          <div class="sol-benefit-visual">
            <img
              class="sol-benefit-img"
              src="./assets/img/FigmaDDSSlicePNG9e1a25e2dc45158cd0efa618810b7841.png"
              :alt="isEnglish ? 'Key benefit metrics' : '关键效益指标提升'"
            />
          </div>
          <div class="sol-chart-caption">
            {{ isEnglish ? 'Key benefit metrics' : '关键效益指标提升' }}
          </div>
        </div>
        <div class="sol-benefit-list">
          <div
            v-for="(b, index) in displayBenefitList"
            :key="'bf_' + index"
            class="sol-benefit-item"
          >
            <div class="sol-benefit-icon">
              <img :src="b.icon" :alt="b.title" />
            </div>
            <div>
              <div class="sol-benefit-title">{{ b.title }}</div>
              <div class="sol-benefit-desc">{{ b.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { siteHomeLink } from '@/utils/lang';

export default {
  name: 'SolutionOffshore',
  data() {
    return {
      overviewList: [
        {
          label: '项目名称',
          labelEn: 'Project Name',
          desc: '东南亚300MW海上风电场塔筒、单桩基础防腐修复工程。',
          descEn: 'Corrosion repair project for towers and monopile foundations of a 300MW offshore wind farm in Southeast Asia.',
          num: '5',
          unit: '年',
          unitEn: 'yrs',
          sub: '投放时间',
          subEn: 'Years in service'
        },
        {
          label: '现状与背景',
          labelEn: 'Current Status',
          desc: '投运5年后塔筒焊缝、浪溅区红锈蔓延，法兰点蚀深度达0.3–0.8mm。',
          descEn: 'After 5 years in service, red rust spread at tower welds and splash zones; flange pitting depth reached 0.3–0.8mm.',
          num: '0.3—0.8',
          unit: 'mm',
          unitEn: 'mm',
          sub: '法兰点蚀深度',
          subEn: 'Flange pitting depth'
        },
        {
          label: '客户核心诉求',
          labelEn: 'Customer Needs',
          desc: '摆脱高成本频繁维保，需一套20年长效免大修防腐体系。',
          descEn: 'Escape costly frequent maintenance with a 20-year long-life system that avoids major overhauls.',
          num: '20',
          unit: '年',
          unitEn: 'yrs',
          sub: '目标设计寿命',
          subEn: 'Target design life'
        }
      ],
      challengeList: [
        {
          title: '防护体系失效',
          titleEn: 'Coating system failure',
          desc: '纯油漆体系缺乏牺牲阳极保护，高温高湿盐雾下漆膜迅速被穿透。',
          descEn: 'Paint-only systems lack sacrificial anode protection; films are quickly penetrated in hot, humid salt fog.',
          icon: require('./assets/img/FigmaDDSSlicePNG0de1c9fd4d7aa4c792204bcefdf6b97c.png')
        },
        {
          title: '应力集中腐蚀',
          titleEn: 'Stress-concentration corrosion',
          desc: '法兰、焊缝等应力集中区漆膜易开裂剥落，氯离子直达基材。',
          descEn: 'Coatings at flanges and welds crack and peel under stress concentration, allowing chlorides to reach the substrate.',
          icon: require('./assets/img/FigmaDDSSlicePNG77df27276745cfc986456d18f0abd6a2.png')
        },
        {
          title: '极端环境加速',
          titleEn: 'Extreme environment acceleration',
          desc: '潮差区反复浸泡与干湿循环，叠加高温盐雾，腐蚀速率远超内陆。',
          descEn: 'Repeated wetting/drying in the tidal zone plus hot salt fog accelerates corrosion far beyond inland rates.',
          icon: require('./assets/img/FigmaDDSSlicePNG4c88ba7646c95d346ea1fa49888203b1.png')
        },
        {
          title: '紫外线老化',
          titleEn: 'UV aging',
          desc: '强紫外线致普通聚氨酯面漆3–5年内粉化龟裂，防护过早失效。',
          descEn: 'Strong UV causes ordinary PU topcoats to chalk and crack within 3–5 years, ending protection early.',
          icon: require('./assets/img/FigmaDDSSlicePNG9c8669bd9bc16d4ef873b3b0d53c3498.png')
        }
      ],
      duplexSteps: [
        {
          title: '表面预处理',
          titleEn: 'Surface prep',
          desc: 'Sa2.5级喷砂除锈',
          descEn: 'Sa2.5 abrasive blast cleaning'
        },
        {
          title: '分层热喷涂',
          titleEn: 'Zoned thermal spray',
          desc: '按区域差异化涂层',
          descEn: 'Zone-differentiated metal coatings'
        },
        {
          title: '多层封闭',
          titleEn: 'Multi-layer sealing',
          desc: '封孔剂+云铁漆+氟碳面漆',
          descEn: 'Sealant + micaceous iron oxide + fluorocarbon topcoat'
        }
      ],
      zoneList: [
        {
          title: '潮差 / 全浸区',
          titleEn: 'Splash / immersion',
          desc: '电弧喷涂 Zn15Al 锌铝合金，厚度220μm；牺牲阳极原理提供高效阴极保护',
          descEn: 'Arc-spray Zn15Al zinc-aluminum alloy, 220μm; sacrificial anode cathodic protection'
        },
        {
          title: '塔筒大气区',
          titleEn: 'Atmospheric tower',
          desc: '电弧喷涂纯锌，厚度130μm；兼顾防护性能与施工经济性',
          descEn: 'Arc-spray pure zinc, 130μm; balances protection performance and application cost'
        },
        {
          title: '高应力区（焊缝/边角）',
          titleEn: 'High-stress areas',
          desc: '额外增加30μm NiAl打底 + 100μm HVAF喷涂WC-Ni面层，提升结合力与耐磨耐蚀性',
          descEn: 'Extra 30μm NiAl bond coat + 100μm HVAF WC-Ni top layer for adhesion, wear and corrosion resistance'
        }
      ],
      benefitList: [
        {
          title: '极致高性价比',
          titleEn: 'Cost efficiency',
          desc: '设备采购成本约为欧美同类产品的1/6',
          descEn: 'Equipment cost about 1/6 of comparable European/US products',
          icon: require('./assets/img/FigmaDDSSlicePNGc269b09b7ad1c355d29463b6b74c17ba.png')
        },
        {
          title: '全场景应用覆盖',
          titleEn: 'Full application coverage',
          desc: '覆盖陆上/海上塔筒、叶片、法兰、轴承等部件',
          descEn: 'Covers onshore/offshore towers, blades, flanges, bearings and more',
          icon: require('./assets/img/FigmaDDSSlicePNG6d3a0503b39e66081d7ea5f1add18104.png')
        },
        {
          title: '东南亚本地化适配',
          titleEn: 'SE Asia localization',
          desc: '针对高温高湿盐雾气候优化封孔与底漆工艺',
          descEn: 'Sealant and primer processes optimized for hot, humid salt-fog climates',
          icon: require('./assets/img/FigmaDDSSlicePNG8778e01da2265e8135ae3dafdc8063dc.png')
        },
        {
          title: '全链条交付服务',
          titleEn: 'Full-chain delivery',
          desc: '设备+锌铝丝材+涂料+海外现场施工指导',
          descEn: 'Equipment + Zn/Al wires + coatings + overseas on-site guidance',
          icon: require('./assets/img/FigmaDDSSlicePNG45036c309c096b908884e009d2467d28.png')
        }
      ]
    };
  },
  computed: {
    isEnglish() {
      return this.$route.query.lang === 'en';
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    pageTitle() {
      return this.isEnglish
        ? 'Long-term Corrosion Protection Solution for Offshore Wind Steel Structures'
        : '海上风电钢结构长效防腐解决方案';
    },
    displayOverviewList() {
      return this.overviewList.map((item) => ({
        label: this.isEnglish ? item.labelEn : item.label,
        desc: this.isEnglish ? item.descEn : item.desc,
        num: item.num,
        unit: this.isEnglish ? item.unitEn : item.unit,
        sub: this.isEnglish ? item.subEn : item.sub
      }));
    },
    displayChallengeList() {
      return this.challengeList.map((item) => ({
        title: this.isEnglish ? item.titleEn : item.title,
        desc: this.isEnglish ? item.descEn : item.desc,
        icon: item.icon
      }));
    },
    displayDuplexSteps() {
      return this.duplexSteps.map((item) => ({
        title: this.isEnglish ? item.titleEn : item.title,
        desc: this.isEnglish ? item.descEn : item.desc
      }));
    },
    displayZoneList() {
      return this.zoneList.map((item) => ({
        title: this.isEnglish ? item.titleEn : item.title,
        desc: this.isEnglish ? item.descEn : item.desc
      }));
    },
    displayBenefitList() {
      return this.benefitList.map((item) => ({
        title: this.isEnglish ? item.titleEn : item.title,
        desc: this.isEnglish ? item.descEn : item.desc,
        icon: item.icon
      }));
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Banner */
.sol-banner {
  width: 100%;
  height: 420px;
  background: url(./assets/img/FigmaDDSSlicePNG57fc43decb6b155499ad9e7b7ae233da.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.sol-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
}

.sol-banner-mask {
  position: relative;
  z-index: 1;
  background: rgba(15, 23, 42, 0.55);
  padding: 28px 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.sol-banner-zh {
  color: #fff;
  font-size: 40px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-banner-en {
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 1px;
}

/* 版心 */
.sol-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 28px 0 80px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sol-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 28px;
}

.sol-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.sol-breadcrumb a:hover {
  color: #1e40af;
}

.sol-crumb-sep {
  color: #9ca3af;
}

.sol-crumb-active {
  color: #374151;
}

.sol-page-title {
  margin: 0;
  font-size: 36px;
  font-weight: 900;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.sol-page-line {
  width: 100%;
  height: 2px;
  background: #1e40af;
  margin: 18px 0 36px;
  opacity: 0.85;
}

.sol-h2 {
  margin: 0 0 20px;
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  line-height: 1.3;
  width: 100%;
  text-align: left;
}

/* 一、项目概况 */
.sol-overview {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  background: #eef4fb;
  border-radius: 10px;
  padding: 36px 28px 32px;
  box-sizing: border-box;
  margin-bottom: 48px;
}

.sol-overview-col {
  padding: 0 28px;
  border-right: 1px solid rgba(148, 163, 184, 0.35);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sol-overview-col:last-child {
  border-right: none;
}

.sol-ov-label {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 14px;
}

.sol-ov-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #4b5563;
  min-height: 72px;
}

.sol-ov-metric {
  margin-top: 20px;
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #1e40af;
}

.sol-ov-num {
  font-size: 42px;
  font-weight: 800;
  line-height: 1;
}

.sol-ov-unit {
  font-size: 20px;
  font-weight: 700;
}

.sol-ov-sub {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
}

/* 二、腐蚀挑战 */
.sol-challenge {
  width: 100%;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 28px;
  margin-bottom: 48px;
  align-items: stretch;
}

.sol-chart-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px 28px 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.sol-chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 16px;
}

.sol-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.sol-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
}

.sol-dot-dark {
  background: #1e3a8a;
}

.sol-dot-teal {
  background: #14b8a6;
}

.sol-chart-body {
  display: flex;
  gap: 10px;
  flex: 1;
  min-height: 260px;
}

.sol-chart-y {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 12px;
  color: #9ca3af;
  padding: 8px 0 18px;
  width: 28px;
  text-align: right;
}

.sol-chart-plot {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
}

.sol-bars {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 56px;
  height: 100%;
  padding: 0 20px 0;
}

.sol-bars-3 {
  gap: 40px;
}

.sol-bar-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  width: 56px;
}

.sol-bar-val {
  font-size: 13px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 6px;
}

.sol-bar {
  width: 48px;
  border-radius: 4px 4px 0 0;
  min-height: 8px;
}

.sol-bar-dark {
  background: #1e3a8a;
}

.sol-bar-teal {
  background: #14b8a6;
}

.sol-chart-xline {
  height: 2px;
  background: #e5e7eb;
  width: 100%;
}

.sol-chart-caption {
  text-align: center;
  font-size: 14px;
  color: #374151;
  margin-top: 14px;
  font-weight: 600;
}

.sol-challenge-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sol-challenge-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  box-sizing: border-box;
}

.sol-challenge-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.sol-challenge-icon img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.sol-challenge-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}

.sol-challenge-desc {
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
}

/* 三、Duplex */
.sol-core-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e40af;
  font-size: 15px;
  margin: -4px 0 18px;
}

.sol-core-tip em {
  font-style: normal;
  font-weight: 700;
}

.sol-core-bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1e40af;
  display: inline-block;
}

.sol-duplex {
  width: 100%;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 32px 36px 28px;
  box-sizing: border-box;
  margin-bottom: 48px;
  background: #f8fbff;
}

.sol-formula {
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-bottom: 28px;
}

.sol-formula-card {
  flex: 1;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 22px 20px;
  box-sizing: border-box;
}

.sol-formula-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
}

.sol-formula-desc {
  font-size: 13px;
  line-height: 1.65;
  color: #6b7280;
}

.sol-formula-op {
  display: flex;
  align-items: center;
  font-size: 28px;
  font-weight: 700;
  color: #1e40af;
  padding: 0 2px;
}

.sol-formula-result {
  background: #1e40af;
  border-color: #1e40af;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.sol-result-num {
  font-size: 48px;
  font-weight: 800;
  line-height: 1;
  color: #fff;
}

.sol-result-num span {
  font-size: 22px;
  margin-left: 2px;
}

.sol-result-desc {
  margin-top: 8px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
}

.sol-steps-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.sol-steps-label {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.sol-steps-arrow {
  color: #1e40af;
  font-size: 18px;
  font-weight: 700;
}

.sol-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.sol-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.sol-step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #1e40af;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sol-step-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.sol-step-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

/* 四、分区 */
.sol-zone {
  width: 100%;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 28px;
  margin-bottom: 48px;
}

.sol-zone-right {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sol-zone-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 18px 20px;
}

.sol-zone-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.sol-zone-desc {
  font-size: 13px;
  line-height: 1.65;
  color: #6b7280;
}

.sol-zone-summary {
  margin-top: auto;
  background: #eff6ff;
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sol-zone-summary strong {
  color: #1e40af;
  font-size: 16px;
}

.sol-zone-summary span {
  color: #4b5563;
  font-size: 13px;
}

/* 五、效益 */
.sol-benefit {
  width: 100%;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 28px;
}

.sol-benefit-visual {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
}

.sol-benefit-img {
  max-width: 90%;
  max-height: 220px;
  object-fit: contain;
}

.sol-benefit-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sol-benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 18px;
}

.sol-benefit-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sol-benefit-icon img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.sol-benefit-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}

.sol-benefit-desc {
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
}

@media (max-width: 1100px) {
  .sol-overview {
    grid-template-columns: 1fr;
  }

  .sol-overview-col {
    border-right: none;
    border-bottom: 1px solid rgba(148, 163, 184, 0.35);
    padding: 20px 8px;
  }

  .sol-overview-col:last-child {
    border-bottom: none;
  }

  .sol-challenge,
  .sol-zone,
  .sol-benefit,
  .sol-formula,
  .sol-steps {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .sol-formula {
    display: flex;
    flex-direction: column;
  }

  .sol-formula-op {
    justify-content: center;
  }
}
</style>
